import React, { useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ExternalLink, Loader2 } from 'lucide-react'
import {
  GetOAuthUrlDocument,
  GetOAuthUrlQuery,
  GetOAuthUrlQueryVariables,
  ToolAuthType,
} from '__generated__/api-types-and-hooks'
import { fetcher } from 'lib/api-client'
import {
  CoachChatContext,
  isCoachSessionId,
} from 'components/Common/AppCoachReportBlock/coachChatContext'

/**
 * A design Coach made in the asker's own Canva account (create_canva_design lays it out itself:
 * `made_by` 'coach'; create_canva_ai_design uses Canva AI credits), or one edit_canva_design
 * changed in place (`changed`). Only its stable links are kept: Canva's thumbnail and export links
 * expire, and the chat never loads remote pictures.
 */
export type CanvaDesignBlock = {
  type: 'canva_design'
  title: string
  design_id: string
  edit_url: string
  view_url?: string
  changed?: boolean
  made_by?: 'coach'
}

/** The note under the design: changed in place, made by Coach, or made with Canva AI credits. */
const designNote = (block: CanvaDesignBlock): string => {
  if (block.changed === true) return 'askCL.canva.changedInPlace'
  if (block.made_by === 'coach') return 'askCL.canva.madeByCoach'
  return 'askCL.canva.credits'
}

/** Shown when the asker has to connect (or connect again) their Canva account first. */
export type ConnectCanvaBlock = {
  type: 'connect_canva'
  reason: 'not_connected' | 'reconnect'
}

// The connector name of the Canva tool (Tools table), sent to getOAuthUrl
const CANVA_CONNECTOR = 'Canva'

// How long "Opening Canva…" stays up after the browser was sent to Canva. A page still here by
// then did not leave (e.g. the person chose to stay on a page with unsaved changes, or pressed
// Esc), so the button works again.
const LEAVE_WAIT_MS = 5000

// ---------------------------------------------------------------------------
// Link checks — saved messages, stream lines and API answers are not trusted,
// so every link is checked before it is offered or followed.
// ---------------------------------------------------------------------------

const httpsUrl = (href: unknown): URL | null => {
  if (typeof href !== 'string') return null
  let url: URL
  try {
    url = new URL(href)
  } catch {
    return null
  }
  return url.protocol === 'https:' && !url.username && !url.password ? url : null
}

/**
 * A link that opens the design in Canva (canva.com, www.canva.com or another canva.com host), or
 * null. It takes every link the backend's canvaLink gives (coach-agent/tools/write/canva-tools.ts:
 * canva.com subdomains only).
 */
export const canvaDesignUrl = (href: unknown): string | null => {
  const url = httpsUrl(href)
  const host = url?.hostname
  return url && (host === 'canva.com' || host?.endsWith('.canva.com')) ? url.href : null
}

/** Canva's sign-in page, as getOAuthUrl answers: the only place Connect Canva sends the browser. */
export const canvaSignInUrl = (href: unknown): string | null => {
  const url = httpsUrl(href)
  return url && url.hostname === 'www.canva.com' ? url.href : null
}

/**
 * Where Canva sends the person back to: this page, with a flash the floating chat reads to open
 * this Coach thread again. URLSearchParams encodes the flash once; FlashParamsProvider decodes it.
 * Never `connectorName`: the Tools, Calendar and other older screens read it as "a tool was just
 * connected" and record the connection a second time.
 */
export const canvaReturnUrl = (pageUrl: string, sessionId?: string | null): string => {
  const url = new URL(pageUrl)
  const flash = 'coachReturn=canva'
  url.searchParams.set(
    'flash',
    isCoachSessionId(sessionId) ? `${flash}&coachSession=${sessionId}` : flash
  )
  return url.toString()
}

// ---------------------------------------------------------------------------
// Components
// ---------------------------------------------------------------------------

const focusRing = 'focus:outline-none focus-visible:appShadow-focus'
// index.css gives every element its own text color, so icons must follow their link or button
const inheritColor = '[color:inherit]'
const primaryAction = `inline-flex min-h-[36px] items-center gap-1.5 rounded-lg bg-app-primary-125 px-3 text-sm font-medium text-white no-underline transition-colors hover:bg-app-primary-120 ${focusRing}`

/** "Works with Canva": compatibility wording only, as Canva asks (never "by Canva"), no logo. */
const WorksWithCanva = () => {
  const { t } = useTranslation()
  return <p className="text-xs font-medium text-app-grey-65">{t('askCL.canva.worksWith')}</p>
}

export const AppCoachCanvaDesignBlock: React.FC<{ block: CanvaDesignBlock }> = ({ block }) => {
  const { t } = useTranslation()
  const editUrl = canvaDesignUrl(block.edit_url)
  const hasDesign = typeof block.design_id === 'string' && block.design_id.trim() !== ''

  if (!editUrl || !hasDesign) {
    return <p className="text-xs text-app-grey-65">{t('askCL.canva.unavailable')}</p>
  }

  const title = typeof block.title === 'string' ? block.title.trim() : ''
  return (
    <div className="[&_svg_*]:[color:inherit]">
      <WorksWithCanva />
      <h4 dir="auto" className="mt-0.5 break-words text-sm font-semibold text-app-grey-100">
        {title || t('askCL.canva.designLabel')}
      </h4>
      <a
        href={editUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={`${primaryAction} mt-3`}
      >
        <ExternalLink aria-hidden="true" className={`h-4 w-4 flex-none ${inheritColor}`} />
        {t('askCL.canva.open')}
      </a>
      <p className="mt-2 text-xs text-app-grey-65">{t(designNote(block))}</p>
    </div>
  )
}

export const AppCoachConnectCanvaBlock: React.FC<{ block: ConnectCanvaBlock }> = ({ block }) => {
  const { t } = useTranslation()
  const { sessionId, connectFailed } = useContext(CoachChatContext)
  const [status, setStatus] = useState<'idle' | 'connecting' | 'failed'>('idle')
  // Clicked since the card was shown: a note about the last sign-in no longer applies
  const [retried, setRetried] = useState(false)
  // The sign-in page of the last click. Trying again reuses it: asking for a new one ends this
  // sign-in on the server, while the browser may still be on its way to Canva with it.
  const signInRef = useRef<string | null>(null)
  const leaveTimerRef = useRef<number>()

  useEffect(() => {
    // Back from Canva with the browser's Back button, the page can come back exactly as it was
    // left. That sign-in may be used up by then, so the next click asks for a new one.
    const reset = (event: PageTransitionEvent) => {
      if (!event.persisted) return
      window.clearTimeout(leaveTimerRef.current)
      signInRef.current = null
      setStatus('idle')
    }
    window.addEventListener('pageshow', reset)
    return () => {
      window.removeEventListener('pageshow', reset)
      window.clearTimeout(leaveTimerRef.current)
    }
  }, [])

  // A full page load: Canva sends the person back here, and the chat opens this thread again
  const leaveFor = (signInUrl: string) => {
    window.location.assign(signInUrl)
    window.clearTimeout(leaveTimerRef.current)
    leaveTimerRef.current = window.setTimeout(() => setStatus('idle'), LEAVE_WAIT_MS)
  }

  const connect = async () => {
    // aria-disabled, not disabled, while it works: a disabled button would drop the focus
    if (status === 'connecting') return
    setRetried(true)
    setStatus('connecting')
    if (signInRef.current) {
      leaveFor(signInRef.current)
      return
    }
    try {
      const data = await fetcher<GetOAuthUrlQuery, GetOAuthUrlQueryVariables>(GetOAuthUrlDocument, {
        input: {
          connectorName: CANVA_CONNECTOR,
          targetOrigin: canvaReturnUrl(window.location.href, sessionId),
          authType: ToolAuthType.Oauth,
        },
      })()
      const signInUrl = data?.getOAuthUrl?.success ? canvaSignInUrl(data.getOAuthUrl.data) : null
      if (!signInUrl) throw new Error('No Canva sign-in page')
      signInRef.current = signInUrl
      leaveFor(signInUrl)
    } catch {
      setStatus('failed')
    }
  }

  const connecting = status === 'connecting'
  return (
    <div className="[&_svg_*]:[color:inherit]">
      <WorksWithCanva />
      <h4 className="mt-0.5 text-sm font-semibold text-app-grey-100">
        {t('askCL.canva.connectTitle')}
      </h4>
      <p className="mt-1 text-sm text-app-grey-75">
        {block.reason === 'reconnect'
          ? t('askCL.canva.reconnectBody')
          : t('askCL.canva.notConnectedBody')}
      </p>
      <button
        type="button"
        onClick={connect}
        aria-disabled={connecting}
        className={`${primaryAction} mt-3 aria-disabled:cursor-wait aria-disabled:opacity-70`}
      >
        {connecting && (
          <Loader2
            aria-hidden="true"
            className={`h-4 w-4 flex-none animate-spin ${inheritColor}`}
          />
        )}
        {connecting ? t('askCL.canva.connecting') : t('askCL.canva.connect')}
      </button>
      {status === 'failed' && (
        <p role="alert" className="mt-2 text-xs text-app-red-80">
          {t('askCL.canva.connectFailed')}
        </p>
      )}
      {/* Back from Canva without a connection: they declined, or the sign-in failed */}
      {!retried && connectFailed === 'canva' && (
        <p role="alert" className="mt-2 text-xs text-app-red-80">
          {t('askCL.canva.notConnected')}
        </p>
      )}
      <p className="mt-2 text-xs text-app-grey-65">{t('askCL.canva.afterConnect')}</p>
    </div>
  )
}
